/**
 * Mirror of backend proposal_models.py `compute_addons` + `compute_option`.
 * Used for live recalculation in the proposal builder and on the public
 * proposal page so totals update instantly without a server round-trip.
 */

export function computeAddons(adHocModules) {
    let extraSetup = 0;
    let extraMonthly = 0;
    for (const m of (adHocModules || [])) {
        if (m?.include_in_pricing === false) continue;
        extraSetup += Number(m?.suggested_setup_price || 0);
        extraMonthly += Number(m?.suggested_monthly_price || 0);
    }
    return {
        extra_setup: round2(extraSetup),
        extra_monthly: round2(extraMonthly),
    };
}

function round2(v) {
    return Math.round((Number(v) || 0) * 100) / 100;
}

/**
 * @param {object} option raw option fields
 * @param {number} vatRate e.g. 20
 * @param {object} addons  { extra_setup, extra_monthly }
 * @param {string|null} startDate ISO date for renewal example
 */
export function computeOption(option, vatRate, addons = { extra_setup: 0, extra_monthly: 0 }, startDate = null) {
    const base_setup      = Number(option?.setup_fee || 0);
    const effective_setup_gross = round2(base_setup + Number(addons.extra_setup || 0));
    const setup_discount_pct = Number(option?.setup_discount_pct || 0);
    const setup_discount_amount = round2(effective_setup_gross * setup_discount_pct / 100);
    const effective_setup = round2(effective_setup_gross - setup_discount_amount);
    const deposit_pct     = Number(option?.deposit_pct || 0);
    const base_monthly    = Number(option?.monthly_saas_fee || 0);
    const effective_monthly = round2(base_monthly + Number(addons.extra_monthly || 0));
    const annual_manual   = option?.annual_saas_fee_manual;
    const length          = parseInt(option?.contract_length_months || 12, 10) || 12;
    const min_months      = option?.minimum_commitment_months;
    const discount_pct    = Number(option?.discount_pct || 0);
    const vat_applies     = option?.vat_applies !== false;
    const frequency       = option?.payment_frequency || 'monthly';

    const annual_gross    = (annual_manual !== null && annual_manual !== undefined && annual_manual !== '')
        ? Number(annual_manual)
        : round2(effective_monthly * 12);
    const discount_amount = round2(annual_gross * discount_pct / 100);
    const annual_net      = round2(annual_gross - discount_amount);

    const include_y2 = !!option?.include_year_2_support;
    const y2_monthly_raw = option?.year_2_support_fee;
    const y2_monthly = (y2_monthly_raw !== null && y2_monthly_raw !== undefined && y2_monthly_raw !== '')
        ? Number(y2_monthly_raw)
        : effective_monthly;
    const y2_annual = round2(y2_monthly * 12);

    const deposit_due     = round2(effective_setup * deposit_pct / 100);
    const year_one_net    = round2(effective_setup + annual_net);
    const min_commit_value = min_months
        ? round2(effective_setup + annual_net * (Number(min_months) / 12))
        : year_one_net;

    const vat_amount      = vat_applies ? round2(year_one_net * vatRate / 100) : 0;
    const total_inc_vat   = round2(year_one_net + vat_amount);
    const balance_after_deposit = round2(total_inc_vat - deposit_due);

    let renewal_example = null;
    try {
        if (startDate) {
            const d = new Date(startDate);
            d.setMonth(d.getMonth() + length);
            renewal_example = d.toISOString().slice(0, 10);
        }
    } catch { /* ignore */ }

    return {
        monthly_total: effective_monthly,
        monthly_base: round2(base_monthly),
        monthly_addons: round2(addons.extra_monthly),
        setup_total: effective_setup,
        setup_total_gross: effective_setup_gross,
        setup_base: base_setup,
        setup_addons: round2(addons.extra_setup),
        setup_discount_pct,
        setup_discount_amount,
        annual_total_gross: annual_gross,
        annual_total_net: annual_net,
        discount_amount,
        year_one_total_net: year_one_net,
        include_year_2_support: include_y2,
        year_2_monthly: include_y2 ? round2(y2_monthly) : 0,
        year_2_annual:  include_y2 ? y2_annual : 0,
        vat_amount,
        total_inc_vat,
        deposit_due,
        balance_after_deposit,
        minimum_commitment_value: min_commit_value,
        renewal_date_example: renewal_example,
        payment_frequency: frequency,
        contract_length_months: length,
    };
}
